import { Sprites } from "./sprites";
// The game's SR defaults: "Split Box → Boundary" (SplitBoxOptions.SplitBoxBoundary, texels either side of the
// sprite's centre; 22.7 is a third of the lid) and "Blend Width" (SplitBoxBlendWidth, the full width).
export const SPLIT_BOUNDARY = 22.7;
const SPLIT_BLEND_WIDTH = 0;
const RAMP_SIZE = 256;
let source = null;
const lids = new Map();
function pixels(img) {
    const c = document.createElement("canvas");
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (!ctx)
        return null;
    ctx.drawImage(img, 0, 0);
    return ctx.getImageData(0, 0, c.width, c.height);
}
// The index sprite and ramps as bytes, read once both have decoded. Null until then (and with no DOM).
function readSource() {
    if (source)
        return source;
    if (typeof document === "undefined")
        return null;
    const indexImg = Sprites.single("boxSplitIndex");
    const rampsImg = Sprites.single("boxSplitRamps");
    if (!indexImg || !rampsImg || rampsImg.naturalWidth !== RAMP_SIZE)
        return null;
    const idx = pixels(indexImg);
    const ramps = pixels(rampsImg);
    if (!idx || !ramps)
        return null;
    const n = idx.width * idx.height;
    const index = new Uint8Array(n);
    const alpha = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
        index[i] = idx.data[i * 4];
        alpha[i] = idx.data[i * 4 + 1];
    }
    source = { w: idx.width, h: idx.height, index, alpha, ramps: ramps.data, rows: ramps.height };
    return source;
}
// sRGB 0..255 -> OKLab, and back (the shader's ToOklab / FromOklab).
function toLinear(v) {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
function toSrgb(l) {
    const c = Math.min(1, Math.max(0, l));
    return 255 * (c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);
}
function toOklab(r, g, b) {
    const lr = toLinear(r), lg = toLinear(g), lb = toLinear(b);
    const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
    const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
    const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
    return [
        0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
        1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
        0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
    ];
}
function fromOklab(L, a, b) {
    const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
    const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
    const s = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3);
    return [
        toSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
        toSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
        toSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
    ];
}
function smoothstep(e0, e1, x) {
    const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
    return t * t * (3 - 2 * t);
}
/**
 * The closed lid of a split box with columns `cols` (left, middle, right), at the sprite's own size — the
 * same 142 px square as the per-colour lids, centred the same way. Built once per colour triple and kept.
 * Null until the index sprite and ramps have decoded (the caller falls back), or for a colour with no ramp.
 */
export function splitLid(cols) {
    const src = readSource();
    if (!src || cols.length < 3)
        return null;
    const rows = [cols[0] - 1, cols[1] - 1, cols[2] - 1];
    if (rows.some((r) => r < 0 || r >= src.rows))
        return null;
    const key = rows.join(",");
    const cached = lids.get(key);
    if (cached)
        return cached;
    const { w, h, index, alpha, ramps } = src;
    const bound = SPLIT_BOUNDARY;
    const soft = Math.min(SPLIT_BLEND_WIDTH / 2, bound - 0.5);
    // The three ramp rows, each entry already in OKLab for the blended texels.
    const lab = rows.map((row) => {
        const out = new Float64Array(RAMP_SIZE * 3);
        for (let e = 0; e < RAMP_SIZE; e++) {
            const o = (row * RAMP_SIZE + e) * 4;
            out.set(toOklab(ramps[o], ramps[o + 1], ramps[o + 2]), e * 3);
        }
        return out;
    });
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx)
        return null;
    const img = ctx.createImageData(w, h);
    for (let x = 0; x < w; x++) {
        const u = x + 0.5 - w / 2;
        const wa = soft < 0.001 ? (u < -bound ? 1 : 0) : 1 - smoothstep(-bound - soft, -bound + soft, u);
        const wc = soft < 0.001 ? (u > bound ? 1 : 0) : smoothstep(bound - soft, bound + soft, u);
        const wb = Math.max(0, 1 - wa - wc);
        const only = wa >= 0.9999 ? 0 : wb >= 0.9999 ? 1 : wc >= 0.9999 ? 2 : -1;
        for (let y = 0; y < h; y++) {
            const i = y * w + x;
            const a = alpha[i];
            if (a === 0)
                continue;
            const e = index[i];
            const o = i * 4;
            if (only >= 0) {
                // Inside one column: that colour's own entry exactly, as the shader does.
                const r = (rows[only] * RAMP_SIZE + e) * 4;
                img.data[o] = ramps[r];
                img.data[o + 1] = ramps[r + 1];
                img.data[o + 2] = ramps[r + 2];
            }
            else {
                const k = e * 3;
                const [r, g, b] = fromOklab(lab[0][k] * wa + lab[1][k] * wb + lab[2][k] * wc, lab[0][k + 1] * wa + lab[1][k + 1] * wb + lab[2][k + 1] * wc, lab[0][k + 2] * wa + lab[1][k + 2] * wb + lab[2][k + 2] * wc);
                img.data[o] = Math.round(r);
                img.data[o + 1] = Math.round(g);
                img.data[o + 2] = Math.round(b);
            }
            img.data[o + 3] = a;
        }
    }
    ctx.putImageData(img, 0, 0);
    lids.set(key, canvas);
    return canvas;
}
